import {DotLottieReact, setWasmUrl} from '@lottiefiles/dotlottie-react';
// @ts-ignore
import dotlottiePlayer from '@lottiefiles/dotlottie-web/dist/dotlottie-player.wasm?url';
import {useTheme} from 'styled-components';

import {Suspense, use, useMemo} from 'react';

import {Integer} from '../../types/Integer';
import {ThemeLottieKey} from '../../utils/foundationTheme';
import {Box} from '../Box/Box';

export interface LottieProps {
  value: ThemeLottieKey;
  minHeight: Integer;
  autoplay?: boolean;
  loop?: boolean;
}

// Prevent loading wasm file from CDN, force local file
setWasmUrl(dotlottiePlayer);

// Cache for loaded Lottie sources with size limit to prevent memory leaks
const lottieSourceCache = new Map<string, Promise<string>>();
const MAX_CACHE_SIZE = 50;

// Simple cache cleanup function - removes oldest entries when limit is exceeded
function cleanupCache() {
  if (lottieSourceCache.size >= MAX_CACHE_SIZE) {
    // Remove the oldest entries (first entries in Map maintain insertion order)
    const entriesToRemove = lottieSourceCache.size - MAX_CACHE_SIZE + 1;
    const keys = Array.from(lottieSourceCache.keys()).slice(0, entriesToRemove);
    keys.forEach((key) => lottieSourceCache.delete(key));
  }
}

// Move cache entry to end by re-inserting (LRU behavior)
function moveToEnd(key: string): void {
  const value = lottieSourceCache.get(key)!;
  lottieSourceCache.delete(key);
  lottieSourceCache.set(key, value);
}

const Renderer = (
  props: {
    loader: Promise<string>;
  } & Omit<LottieProps, 'value' | 'minHeight'>
) => {
  const source = use<string>(props.loader);
  return <DotLottieReact src={source} {...props} />;
};

export function Lottie(props: LottieProps) {
  const {value, minHeight, ...rest} = props;
  const theme = useTheme();

  // Memoize the loader to prevent recreation on re-renders
  const loader = useMemo(() => {
    // Check if we already have this loader cached
    if (lottieSourceCache.has(value)) {
      moveToEnd(value);
      return lottieSourceCache.get(value)!;
    }

    // Create the loader and cache it
    const loaderFn = theme.lotties?.[value];
    if (!loaderFn) {
      return null;
    }

    const loaderPromise = loaderFn();

    // Clean up cache before adding new entry
    cleanupCache();
    lottieSourceCache.set(value, loaderPromise);

    // Remove failed promises from cache to allow retry attempts
    loaderPromise.catch(() => {
      lottieSourceCache.delete(value);
    });

    return loaderPromise;
  }, [value, theme.lotties]);

  if (!loader) {
    return null;
  }

  return (
    <Suspense fallback={<Box minHeight={minHeight} />}>
      <Renderer loader={loader} {...rest} />
    </Suspense>
  );
}
